import * as _React from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {ActivityIndicator, StyleSheet, Text, View} from 'react-native'

// @ts-ignore - JS module, no types
import {loadJitsiExternalApi} from '../../../../web/jitsi-loader'

declare global {
  interface Window {
    JitsiMeetExternalAPI?: any
  }
}

interface JitsiGuestMeetingProps {
  roomSlug: string
  ownerUsername: string
  onClose: () => void
  onOwnerLeft?: (sessionId?: string) => void
  onSessionId?: (sessionId: string) => void
}

export function JitsiGuestMeeting({
  roomSlug,
  ownerUsername,
  onClose,
  onOwnerLeft,
  onSessionId,
}: JitsiGuestMeetingProps) {
  const [waitingForHost, setWaitingForHost] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const jitsiApiRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  const onOwnerLeftRef = useRef(onOwnerLeft)
  const onSessionIdRef = useRef(onSessionId)
  const sessionIdRef = useRef<string | null>(null)

  useEffect(() => {
    onCloseRef.current = onClose
    onOwnerLeftRef.current = onOwnerLeft
    onSessionIdRef.current = onSessionId
  }, [onClose, onOwnerLeft, onSessionId])

  // Poll room status until the host is present
  useEffect(() => {
    let cancelled = false

    const checkStatus = async () => {
      try {
        const res = await fetch(
          `/api/personal-room/status?roomSlug=${encodeURIComponent(roomSlug)}`,
        )
        if (res.ok) {
          const data = await res.json()
          if (data.active && !cancelled) {
            if (data.sessionId) {
              sessionIdRef.current = data.sessionId
              onSessionIdRef.current?.(data.sessionId)
            }
            setWaitingForHost(false)
            return true
          }
        }
      } catch {}
      return false
    }

    const poll = async () => {
      const active = await checkStatus()
      if (!active && !cancelled) {
        const interval = setInterval(async () => {
          const isActive = await checkStatus()
          if (isActive || cancelled) {
            clearInterval(interval)
          }
        }, 3000)
        return () => clearInterval(interval)
      }
    }

    const cleanup = poll()
    return () => {
      cancelled = true
      cleanup?.then?.(fn => fn?.())
    }
  }, [roomSlug])

  const generateRoomName = useCallback(() => {
    return roomSlug
  }, [roomSlug])

  useEffect(() => {
    if (waitingForHost) return
    if (typeof window === 'undefined') return

    if (!containerRef.current) return
    if (jitsiApiRef.current) return

    const initJitsi = async () => {
      // Fetch a guest JWT — no auth required. The worker picks the backend
      // (JaaS or self-hosted VPS) and returns the matching domain + room name.
      const tokenRes = await fetch('/api/jitsi/guest-token', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          roomSlug,
          guestId: localStorage.getItem('batesky_guest_id') || undefined,
        }),
      })

      if (!tokenRes.ok) {
        const errorData = await tokenRes.json().catch(() => null)
        if (errorData?.code === 'OWNER_NOT_PRESENT') {
          // Owner left between status check and token request — go back to waiting
          setWaitingForHost(true)
          return
        }
        if (errorData?.code === 'ROOM_FULL') {
          setError('This 1:1 room is currently full (2 participants).')
          setLoading(false)
          return
        }
        console.error('Failed to get guest Jitsi token:', errorData)
        setError('Failed to connect to room.')
        setLoading(false)
        return
      }

      const {
        token,
        domain,
        roomName: tokenRoomName,
        scriptUrl,
      } = await tokenRes.json()
      const jitsiRoomName = tokenRoomName || generateRoomName()

      try {
        await loadJitsiExternalApi(
          scriptUrl || `https://${domain}/external_api.js`,
        )
        if (!window.JitsiMeetExternalAPI) {
          console.error('Jitsi Meet External API failed to load')
          setError('Failed to connect to room.')
          setLoading(false)
          return
        }
        if (!containerRef.current) return
        const roomDisplayName = `${ownerUsername}'s Room`

        // Safari requires the `allow` attribute on the iframe BEFORE it loads,
        // otherwise getUserMedia() is permanently blocked in the cross-origin
        // frame. Monkey-patch createElement so the Jitsi API's iframe gets the
        // attribute at creation time.
        const origCreateElement = document.createElement.bind(document)
        document.createElement = function (
          tagName: string,
          options?: ElementCreationOptions,
        ) {
          const el = origCreateElement(tagName, options)
          if (tagName.toLowerCase() === 'iframe') {
            ;(el as HTMLIFrameElement).allow =
              'camera; microphone; display-capture; autoplay; clipboard-write; speaker-selection'
          }
          return el
        } as typeof document.createElement

        const options = {
          roomName: jitsiRoomName,
          jwt: token,
          width: '100%',
          height: '100%',
          parentNode: containerRef.current,

          configOverwrite: {
            // Enable prejoin so guest can enter their name
            prejoinConfig: {
              enabled: true,
              hideExtraJoinButtons: ['no-audio', 'by-phone'],
            },
            prejoinPageEnabled: true,

            disableDeepLinking: true,

            hangupMenuEnabled: false,
            enableEndConference: false,
            disableEndConference: true,

            startWithAudioMuted: true,
            startWithVideoMuted: true,

            // Disable all notification sounds to prevent setSinkId errors in iframe
            disabledSounds: [
              'ASKED_TO_UNMUTE_SOUND',
              'E2EE_OFF_SOUND',
              'E2EE_ON_SOUND',
              'INCOMING_MSG_SOUND',
              'KNOCKING_PARTICIPANT_SOUND',
              'LIVE_STREAMING_OFF_SOUND',
              'LIVE_STREAMING_ON_SOUND',
              'NO_AUDIO_SIGNAL_SOUND',
              'NOISY_AUDIO_INPUT_SOUND',
              'OUTGOING_CALL_EXPIRED_SOUND',
              'OUTGOING_CALL_REJECTED_SOUND',
              'OUTGOING_CALL_RINGING_SOUND',
              'OUTGOING_CALL_START_SOUND',
              'PARTICIPANT_JOINED_SOUND',
              'PARTICIPANT_LEFT_SOUND',
              'RAISE_HAND_SOUND',
              'REACTION_SOUND',
              'RECORDING_OFF_SOUND',
              'RECORDING_ON_SOUND',
              'TALK_WHILE_MUTED_SOUND',
            ],

            subject: roomDisplayName,

            toolbarButtons: [
              'microphone',
              'camera',
              'chat',
              'tileview',
              '__hangup',
            ],

            polls: {enabled: false},
            hiddenPremeetingButtons: ['polls'],
            disablePolls: true,
            chatButtons: ['chat'],

            buttonsWithNotifyClick: ['__hangup'],

            customToolbarButtons: [
              {
                icon: 'https://img.icons8.com/material/96/ffffff/end-call.png',
                id: '__hangup',
                text: 'Leave Room',
                backgroundColor: '#e53935',
              },
            ],
          },

          interfaceConfigOverwrite: {
            DEFAULT_BACKGROUND: '#000000',
            SETTINGS_SECTIONS: ['language', 'profile', 'sounds'],
          },
        }

        const api = new window.JitsiMeetExternalAPI(domain, options)

        // Restore original createElement now that the iframe has been created
        document.createElement = origCreateElement

        jitsiApiRef.current = api

        // With prejoin enabled, hide the loading overlay once Jitsi iframe loads
        // (the user needs to see and interact with the prejoin screen)
        api.addListener('browserSupport', () => {
          setLoading(false)
          clearTimeout((api as any)._loadingTimeout)
        })

        // Also hide loading after a short delay as fallback since
        // browserSupport may not fire on all Jitsi versions
        setTimeout(() => {
          setLoading(false)
        }, 3000)

        // Patch iframe permissions for iOS Safari/Chrome
        // Hangup button handler
        api.addListener('toolbarButtonClicked', (event: any) => {
          if (event.key === '__hangup') {
            api.executeCommand('hangup')
          }
        })

        // Track whether the guest initiated leaving
        let guestIsLeaving = false

        // Helper to dispose and notify when guest is alone (owner not present)
        const disposeAndNotifyOwnerLeft = () => {
          if (guestIsLeaving) return // Don't trigger if guest left voluntarily
          clearTimeout((api as any)._loadingTimeout)
          clearInterval((api as any)._aloneCheckInterval)

          api.dispose()
          jitsiApiRef.current = null
          if (onOwnerLeftRef.current) {
            onOwnerLeftRef.current(sessionIdRef.current || undefined)
          } else {
            onCloseRef.current()
          }
        }

        api.addEventListeners({
          videoConferenceJoined: () => {
            setLoading(false)
            clearTimeout((api as any)._loadingTimeout)
            api.executeCommand('subject', roomDisplayName)

            // Check if guest is alone — owner must be present
            const checkAlone = () => {
              if (guestIsLeaving) return
              const n = api.getNumberOfParticipants()
              if (n <= 1) {
                disposeAndNotifyOwnerLeft()
              }
            }
            // Check immediately after joining
            setTimeout(checkAlone, 2000)
            // Keep checking periodically in case the owner never joined
            ;(api as any)._aloneCheckInterval = setInterval(checkAlone, 5000)
          },
          readyToClose: () => {
            guestIsLeaving = true
            clearInterval((api as any)._aloneCheckInterval)
            onCloseRef.current()
          },
          videoConferenceLeft: () => {
            guestIsLeaving = true
            clearInterval((api as any)._aloneCheckInterval)
            onCloseRef.current()
          },
          participantLeft: () => {
            if (guestIsLeaving) return
            // When the room owner leaves, immediately dispose and show ended screen
            const numParticipants = api.getNumberOfParticipants()
            if (numParticipants <= 1) {
              disposeAndNotifyOwnerLeft()
            }
          },
        })

        api.addListener('videoConferenceFailed', () => {
          setLoading(false)
          setError('Failed to connect to room. Please try again.')
        })

        const loadingTimeout = setTimeout(() => {
          setLoading(false)
          setError('Room took too long to load. Please try again.')
        }, 30000)
        ;(api as any)._loadingTimeout = loadingTimeout
      } catch (err) {
        console.error('Error initializing Jitsi Meet API:', err)
        setError('Failed to initialize video room.')
        setLoading(false)
      }
    }

    initJitsi()

    return () => {
      if (jitsiApiRef.current) {
        clearTimeout((jitsiApiRef.current as any)._loadingTimeout)
        clearInterval((jitsiApiRef.current as any)._aloneCheckInterval)
        ;(jitsiApiRef.current as any)._iframePermObserver?.disconnect()
        jitsiApiRef.current.dispose()
        jitsiApiRef.current = null
      }
    }
  }, [waitingForHost, roomSlug, generateRoomName, ownerUsername])

  if (waitingForHost) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#fd7c21" />
          <Text style={styles.loadingText}>Waiting for host...</Text>
          <Text style={styles.waitingSubtext}>
            {ownerUsername}'s room will open when they join.
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <div
        ref={containerRef}
        className="jitsi-container"
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000',
          zIndex: 9999,
        }}
      />
      {loading && !error && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#fd7c21" />
          <Text style={styles.loadingText}>Loading Room</Text>
        </View>
      )}
      {error && (
        <View style={styles.loadingOverlay}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    width: '100%',
    backgroundColor: '#000',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
  },
  loadingText: {
    color: '#fff',
    marginTop: 12,
    fontSize: 16,
  },
  waitingSubtext: {
    color: '#888',
    marginTop: 8,
    fontSize: 14,
    textAlign: 'center',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 16,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
})
